/**
 * TeamHistorySection
 * Displays a timeline of team member assignments and removals
 */
import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const ROLE_COLORS = {
  sid: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  asid: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  site_draughter: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
  draughter_2d: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  designer_3d: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' }
};

const ROLE_LABELS = {
  sid: 'SID',
  asid: 'ASID',
  site_draughter: 'Site Draughter',
  draughter_2d: '2D Draughter',
  designer_3d: '3D Designer'
};

function TeamHistorySection({ projectId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (projectId) {
      fetchHistory();
    }
  }, [projectId]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/design-projects/${projectId}/team/history`);
      setHistory(response.data.history || []);
    } catch (err) {
      console.error('Error fetching team history:', err);
      setError('Failed to load team history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-MY', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-MY', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDurationText = (durationDays) => {
    if (durationDays === undefined || durationDays === null) return '';
    if (durationDays === 0) return '(same day)';
    if (durationDays === 1) return '(1 day)';
    return `(${durationDays} days)`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex flex-col items-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
          <p className="text-xs text-gray-500">Loading history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        {error}
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <svg className="w-10 h-10 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm">No team assignment history</p>
      </div>
    );
  }

  // Group history by role for better display
  const groupedHistory = history.reduce((acc, entry) => {
    const role = entry.roleType;
    if (!acc[role]) acc[role] = [];
    acc[role].push(entry);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Assignment History
        </h4>
        <span className="text-xs text-gray-500">
          {history.filter(h => h.isActive).length} active / {history.length} total
        </span>
      </div>

      {/* Timeline view */}
      <div className="space-y-3">
        {Object.entries(groupedHistory).map(([roleType, entries]) => {
          const colors = ROLE_COLORS[roleType] || ROLE_COLORS.site_draughter;
          const label = ROLE_LABELS[roleType] || roleType;

          return (
            <div key={roleType} className={`border ${colors.border} rounded-lg overflow-hidden`}>
              {/* Role header */}
              <div className={`px-3 py-2 ${colors.bg} border-b ${colors.border}`}>
                <span className={`text-xs font-medium ${colors.text}`}>{label}</span>
                <span className="text-xs text-gray-500 ml-2">
                  ({entries.filter(e => e.isActive).length} active)
                </span>
              </div>

              {/* Entries */}
              <div className="divide-y divide-gray-100">
                {entries.map((entry, index) => (
                  <div
                    key={entry.id}
                    className={`px-3 py-2 ${entry.isActive ? 'bg-white' : 'bg-gray-50'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {/* Status indicator */}
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          entry.isActive ? 'bg-green-500' : 'bg-gray-400'
                        }`} />

                        {/* User name */}
                        <div className="min-w-0">
                          <span className={`text-sm font-medium ${entry.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                            {entry.user?.nickname || entry.user?.fullName || 'Unknown'}
                          </span>
                          {!entry.isActive && (
                            <span className="ml-2 text-xs px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded">
                              Removed
                            </span>
                          )}
                          {entry.notes && entry.notes.includes('Replaced') && (
                            <span className="ml-2 text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                              Replacement
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Active badge */}
                      {entry.isActive && (
                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full flex-shrink-0">
                          Current
                        </span>
                      )}
                    </div>

                    {/* Assignment details */}
                    <div className="mt-1 ml-4 text-xs text-gray-500 space-y-0.5">
                      <div className="flex items-center gap-1">
                        <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span>
                          Assigned {formatDate(entry.assignedAt)}
                          {entry.assignedBy && (
                            <span className="text-gray-400">
                              {' '}by {entry.assignedBy.nickname || entry.assignedBy.fullName}
                            </span>
                          )}
                        </span>
                      </div>

                      {!entry.isActive && entry.removedAt && (
                        <div className="flex items-center gap-1">
                          <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                          </svg>
                          <span>
                            Removed {formatDate(entry.removedAt)}
                            {entry.removedBy && (
                              <span className="text-gray-400">
                                {' '}by {entry.removedBy.nickname || entry.removedBy.fullName}
                              </span>
                            )}
                            {entry.durationDays !== undefined && (
                              <span className="text-gray-400 ml-1">
                                {getDurationText(entry.durationDays)}
                              </span>
                            )}
                          </span>
                        </div>
                      )}

                      {entry.notes && (
                        <div className="flex items-start gap-1 mt-1">
                          <svg className="w-3 h-3 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                          </svg>
                          <span className="text-gray-400 italic">{entry.notes}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TeamHistorySection;
